import { MutationFunction } from '@apollo/client'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { FC, useState } from 'react'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { useEffectOnlyAfterMount } from 'shared/hooks/useEffectAfterMount'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'

const COMPANY_PREFERENCE_LIMIT = 3
const OTHER_VALUE = 'Other'

interface CompanyPreferences {
    preferenceCompanyId: string
    preferenceCompany: {
        id: string
        value: string
        bucket?: string | null
    }
    order: number
    customValue?: string | null
}

interface CompanyProps {
    companyOptions: DropdownOption[]
    updatedAt: string
    updatedBy: string
    candidateId?: string
    handleUpdate: MutationFunction<any, any>
    companyPreferences: CompanyPreferences[]
}

const getCustomValueForTier = (
    prefs: CompanyPreferences[],
    order: number
): string => {
    const otherPref = prefs.find(
        (p) => p.order === order && p.preferenceCompany.value === OTHER_VALUE
    )
    return otherPref?.customValue || ''
}

const Company: FC<CompanyProps> = ({
    companyPreferences,
    companyOptions,
    updatedAt,
    updatedBy,
    handleUpdate,
    candidateId,
}) => {
    const [primaryCompanies, setPrimaryCompanies] = useState<DropdownOption[]>(
        companyPreferences
            .filter((company) => company.order === 1)
            .map((company) => company.preferenceCompany)
    )
    const [secondaryCompanies, setSecondaryCompanies] = useState<
        DropdownOption[]
    >(
        companyPreferences
            .filter((company) => company.order === 2)
            .map((company) => company.preferenceCompany)
    )

    const [customValues, setCustomValues] = useState<Record<number, string>>({
        1: getCustomValueForTier(companyPreferences, 1),
        2: getCustomValueForTier(companyPreferences, 2),
    })

    const buildPreferences = (companies: DropdownOption[], order: number) =>
        companies.map((company) => ({
            preferenceCompanyId: company.id,
            order,
            ...(company.value === OTHER_VALUE && customValues[order]
                ? { customValue: customValues[order] }
                : {}),
        }))

    const handleSave = () => {
        handleUpdate({
            variables: {
                candidateId,
                preferences: [
                    ...buildPreferences(primaryCompanies, 1),
                    ...buildPreferences(secondaryCompanies, 2),
                ],
            },
        })
    }

    useEffectOnlyAfterMount(() => {
        handleSave()
    }, [primaryCompanies, secondaryCompanies])

    const tiers = [
        {
            label: '1st Choice',
            companies: primaryCompanies,
            setter: setPrimaryCompanies,
            order: 1,
        },
        {
            label: '2nd Choice',
            companies: secondaryCompanies,
            setter: setSecondaryCompanies,
            order: 2,
        },
    ]

    return (
        <>
            {tiers.map(({ label, companies, setter, order }) => (
                <Box key={order} sx={{ marginBottom: getPxAsRem(10) }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {label}
                    </Typography>
                    <Autocomplete
                        multiple
                        fullWidth
                        disableClearable
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        onChange={(_event, newValue) => {
                            if (newValue.length <= COMPANY_PREFERENCE_LIMIT) {
                                setter(newValue)
                                if (
                                    !newValue.some(
                                        (v) => v.value === OTHER_VALUE
                                    )
                                ) {
                                    setCustomValues((prev) => ({
                                        ...prev,
                                        [order]: '',
                                    }))
                                }
                            }
                        }}
                        value={companies}
                        options={companyOptions}
                        getOptionLabel={(option) => option.value}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                placeholder=""
                            />
                        )}
                        getOptionDisabled={(option) =>
                            companies.length >= COMPANY_PREFERENCE_LIMIT &&
                            !companies.some(
                                (item) => item.value === option.value
                            )
                        }
                    />
                    {companies.some((c) => c.value === OTHER_VALUE) && (
                        <TextField
                            fullWidth
                            label="Enter firm name"
                            value={customValues[order]}
                            onChange={(e) =>
                                setCustomValues((prev) => ({
                                    ...prev,
                                    [order]: e.target.value,
                                }))
                            }
                            onBlur={() => handleSave()}
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>
            ))}
            <UpdatedText updatedAt={updatedAt} updatedBy={updatedBy} />
        </>
    )
}

export default Company
